import * as _ from 'lodash';
import { repairConstants } from 'home/HomePage/constants/constants';
import ActivityTypes from '../actions/activity.types';

const intialState = {
  loading: true,
  loadingDetails: false,
  list: [],
  observation_list: [],
  nextPage: 1,
  error: null,
  hasMore: true,
  commentEditLoading: false,
  commentDeleteLoading: false,
  // Grouped view state. `groups` holds machine summaries (aggregates, no activity
  // rows). `groupActivities` holds the rows fetched for the groups the user has
  // actually expanded, keyed by machine id.
  groups: [],
  groupsLoading: false,
  groupActivities: {}
};

// Activities live in THREE places: the flat list, the under-observation list,
// and, in grouped view, one bucket per expanded machine. Every per-activity
// update has to reach all three. Touching `state.list` alone is why commenting,
// opening details, marking as read and deleting all worked in the individual
// view and did nothing at all inside an expanded group: the row the user was
// looking at was never in `list`.
//
// The three collections are built from separate responses, so no activity object
// is shared between them and an in-place update cannot be applied twice.
const mapActivities = (state, fn) => ({
  list: _.map(state.list, fn),
  observation_list: _.map(state.observation_list, fn),
  groupActivities: _.mapValues(state.groupActivities, group => ({
    ...group,
    items: _.map(group.items || [], fn)
  }))
});

const rejectActivities = (state, predicate) => ({
  list: _.reject(state.list, predicate),
  observation_list: _.reject(state.observation_list, predicate),
  groupActivities: _.mapValues(state.groupActivities, group => ({
    ...group,
    items: _.reject(group.items || [], predicate)
  }))
});

// A group's header is server-computed and only rewritten by a full group
// reload, but marking a row read or deleting one changes what the header
// describes. `markAsRead` deliberately does not re-search, so without this the
// header kept saying "Unread: 5" after the user had just read one, and a delete
// left the total, the type chip and "Show more (N remaining)" all one too high.
// The pre-redesign grouped view derived the header from the rows it held, so it
// updated instantly; this keeps that.
const applyGroupDeltas = (groups, deltas) => {
  if (!deltas.size) return groups;
  return _.map(groups, (group) => {
    const delta = deltas.get(group.machine_id);
    if (!delta) return group;
    const typeCounts = { ...group.type_counts };
    _.forEach(delta.types || {}, (change, key) => {
      typeCounts[key] = Math.max((typeCounts[key] || 0) + change, 0);
    });
    return {
      ...group,
      activity_count: Math.max((group.activity_count || 0) + (delta.total || 0), 0),
      unread_count: Math.max((group.unread_count || 0) + (delta.unread || 0), 0),
      type_counts: typeCounts
    };
  });
};

// Must run BEFORE mapActivities, which flips `read` in place.
const readDeltas = (state, payload, action) => {
  const deltas = new Map();
  _.forEach(state.groupActivities, (group, machineId) => {
    let unread = 0;
    _.forEach(group.items || [], (activity) => {
      const match = payload.find(
        a => a.type === activity.type && a.activity_id === activity.activity_id
      );
      if (!match) return;
      if (action === 'read' && activity.read === false) unread -= 1;
      if (action === 'unread' && activity.read === true) unread += 1;
    });
    if (unread !== 0) deltas.set(Number(machineId), { unread });
  });
  return deltas;
};

// Ids are per table, so the type is part of an activity's identity. The delete
// actions carry only an id, and matching on that alone removed every other type
// with the same id from the lists and from the group headers.
const isActivity = (type, id) => activity => activity.type === type && activity.activity_id === id;

// What a delete leaves "Show more (N remaining)" counting from: the bucket's
// total from the server, less the rows just removed from it.
const shrinkBuckets = (groupActivities, deltas) => _.mapValues(groupActivities, (group, machineId) => {
  const delta = deltas.get(Number(machineId));
  if (!delta || !_.isNumber(group.total_count)) return group;
  return { ...group, total_count: Math.max(group.total_count + delta.total, 0) };
});

const activityKey = activity => `${activity.type}-${activity.activity_id}`;

// Groups and rows are paged by offset, so a page can repeat what an earlier one
// held when the feed moves underneath it (a new activity, a re-ranked group).
const appendUnique = (held, incoming, key) => {
  const seen = new Set(_.map(held, key));
  return [...held, ..._.filter(incoming, item => !seen.has(key(item)))];
};

// Must run BEFORE rejectActivities.
const removalDeltas = (state, predicate) => {
  const deltas = new Map();
  _.forEach(state.groupActivities, (group, machineId) => {
    const removed = _.filter(group.items || [], predicate);
    if (!removed.length) return;
    const delta = { total: -removed.length, unread: 0, types: {} };
    _.forEach(removed, (activity) => {
      if (!activity.read) delta.unread -= 1;
      const key = activity.type === 'event' ? activity.event_type : activity.type;
      if (key) delta.types[key] = (delta.types[key] || 0) - 1;
    });
    deltas.set(Number(machineId), delta);
  });
  return deltas;
};

const removeActivities = (state, predicate) => {
  const deltas = removalDeltas(state, predicate);
  const rest = rejectActivities(state, predicate);
  return {
    ...state,
    ...rest,
    groups: applyGroupDeltas(state.groups, deltas),
    groupActivities: shrinkBuckets(rest.groupActivities, deltas)
  };
};

// Is this the response the group is waiting for? A change of filters wipes every
// group's rows, and a newer request for the same group takes its place. A
// response that lands after either belongs to rows the user is no longer looking
// at: written in, it showed the old filter's rows under the new header, and its
// presence stopped the group from ever loading the right ones.
const awaitsGroupResponse = (state, payload) => {
  const bucket = state.groupActivities[payload.machineId];
  return Boolean(bucket) && bucket.requestId === payload.requestId;
};

export default (state = intialState, action) => {
  switch (action.type) {
    case ActivityTypes.GET_EVENTS_REQUEST:
      return {
        ...state,
        list: action.newContext ? [] : [...state.list],
        // Kept while a later page loads, so a failed one does not take the
        // watched events at the top of the feed with it.
        observation_list: action.newContext ? [] : state.observation_list,
        loading: true
      };
    case ActivityTypes.GET_EVENTS_SUCCESS:
      return {
        ...state,
        loading: false,
        ...action.events
      };
    case ActivityTypes.GET_GROUPS_REQUEST:
      // A refresh keeps the groups on screen until their replacement lands.
      // Emptying them unmounted every group, so suspending an event inside an
      // open group collapsed them all and scrolled to the top. The rows are
      // still wiped: an open group then sees it has none and loads them again
      // under the new filters.
      return {
        ...state,
        groupActivities: action.newContext ? {} : state.groupActivities,
        groupsRequestId: action.requestId,
        groupsLoading: true,
        loading: true
      };
    case ActivityTypes.GET_GROUPS_SUCCESS: {
      if (action.requestId !== state.groupsRequestId) return state;
      const { page, groups, ...rest } = action.groups;
      return {
        ...state,
        ...rest,
        groups: page === 1
          ? groups
          : appendUnique(state.groups, groups, group => group.machine_id),
        groupsLoading: false,
        loading: false
      };
    }
    case ActivityTypes.GET_GROUPS_FAILURE:
      if (action.requestId !== state.groupsRequestId) return state;
      // A failed later page keeps the pages already shown, and stops the
      // scroll from asking again.
      return {
        ...state,
        groups: action.page === 1 ? [] : state.groups,
        hasMore: false,
        groupsLoading: false,
        loading: false,
        error: action.error && action.error.message
      };
    case ActivityTypes.GET_GROUP_ACTIVITIES_REQUEST:
      return {
        ...state,
        groupActivities: {
          ...state.groupActivities,
          [action.payload.machineId]: {
            ...(state.groupActivities[action.payload.machineId] || { items: [] }),
            requestId: action.payload.requestId,
            failed: false,
            loading: true
          }
        }
      };
    case ActivityTypes.GET_GROUP_ACTIVITIES_SUCCESS:
      if (!awaitsGroupResponse(state, action.payload)) return state;
      return {
        ...state,
        groupActivities: {
          ...state.groupActivities,
          [action.payload.machineId]: {
            // Page 1 replaces, later pages append: "Show more" extends the list
            // the user is already looking at.
            items: action.payload.page === 1
              ? action.payload.items
              : appendUnique(
                (state.groupActivities[action.payload.machineId] || {}).items || [],
                action.payload.items,
                activityKey
              ),
            page: action.payload.page,
            hasMore: action.payload.hasMore,
            total_count: action.payload.total_count,
            loading: false
          }
        }
      };
    case ActivityTypes.GET_GROUP_ACTIVITIES_FAILURE:
      if (!awaitsGroupResponse(state, action.payload)) return state;
      return {
        ...state,
        groupActivities: {
          ...state.groupActivities,
          [action.payload.machineId]: {
            ...(state.groupActivities[action.payload.machineId] || { items: [] }),
            loading: false,
            // The group offers a retry instead of sitting open and empty.
            failed: true,
            error: action.payload.error && action.payload.error.message
          }
        }
      };
    case ActivityTypes.GET_EVENTS_FAILURE:
      // A failed later page keeps what the user has already scrolled through
      // and stops the scroll from asking again.
      if (action.paginated) {
        return {
          ...state,
          hasMore: false,
          loading: false,
          error: action.error.message
        };
      }
      return {
        ...state,
        list: [],
        observation_list: [],
        loading: false,
        error: action.error.message
      };
    case ActivityTypes.GET_ACTIVITY_COMMENTS_REQUEST:
      return {
        ...state,
        ...mapActivities(state, (activity) => {
          if (activity.activity_id === action.activity_id && activity.type === action.activity_type) {
            activity.loading = true;
          }
          return activity;
        })
      };
    case ActivityTypes.GET_ACTIVITY_COMMENTS_SUCCESS:
      return {
        ...state,
        loadingDetails: false,
        ...mapActivities(state, (activity) => {
          if (activity.activity_id === action.item.activity_id && activity.type === action.item.activity_type) {
            activity.comments = action.item.details.items;
            activity.comment_count = action.item.details.total_count;
            activity.loading = false;
          }
          return activity;
        })
      };
    case ActivityTypes.GET_ACTIVITY_COMMENTS_FAILURE:
      return {
        ...state,
        ...mapActivities(state, (activity) => {
          if (activity.activity_id === action.error.activity_id && activity.type === action.error.activity_type) {
            activity.loading = false;
            activity.error = action.error.error.message;
          }
          return activity;
        })
      };

    case ActivityTypes.GET_NOTE_DETAILS_REQUEST:
      return {
        ...state,
        ...mapActivities(state, (activity) => {
          if (activity.activity_id === action.id && activity.type === action.activity_type) {
            activity.loading = true;
          }
          return activity;
        })
      };
    case ActivityTypes.GET_NOTE_DETAILS_SUCCESS:
      return {
        ...state,
        loadingDetails: false,
        ...mapActivities(state, (activity) => {
          if (activity.activity_id === action.note.id && activity.type === action.note.activity_type) {
            activity.details = action.note.details;
            activity.user_id = action.note.details && action.note.details.user_id;
            activity.loading = false;
          }
          return activity;
        })
      };
    case ActivityTypes.GET_NOTE_DETAILS_FAILURE:
      return {
        ...state,
        ...mapActivities(state, (activity) => {
          if (activity.activity_id === action.error.id && activity.type === action.error.activity_type) {
            activity.loading = false;
            activity.error = action.error.error.message;
          }
          return activity;
        })
      };

    case repairConstants.UPDATE_REPAIR_SUCCESS:
      return {
        ...state,
        loadingDetails: false,
        ...mapActivities(state, (activity) => {
          if (activity.activity_id === action.repair.id && activity.type === action.repair.activity_type) {
            activity = { ...activity, ...action.repair, loading: false };
          }
          return activity;
        })
      };

    case ActivityTypes.GET_EVENT_DETAILS_REQUEST:
      return {
        ...state,
        ...mapActivities(state, (activity) => {
          if (activity.activity_id === action.id && activity.type === action.activity_type) {
            activity.loading = true;
          }
          return activity;
        })
      };
    case ActivityTypes.GET_EVENT_DETAILS_SUCCESS:
      // find event with correct id and update its details section.
      return {
        ...state,
        loadingDetails: false,
        ...mapActivities(state, (activity) => {
          if (activity.activity_id === action.event.id && activity.type === action.event.activity_type) {
            activity.details = action.event.details;
            activity.loading = false;
          }
          return activity;
        })
      };
    case ActivityTypes.GET_EVENT_DETAILS_FAILURE:
      return {
        ...state,
        ...mapActivities(state, (activity) => {
          if (activity.activity_id === action.error.id && activity.type === action.error.activity_type) {
            activity.loading = false;
            activity.error = action.error.error.message;
          }
          return activity;
        })
      };

      // TBD if some redux state changes needed in these events (post_event_comment and close_event.
      // conceptual thing to decide
    case ActivityTypes.POST_ACTIVITY_COMMENT_REQUEST:
      return {
        ...state
      };

    case ActivityTypes.POST_ACTIVITY_COMMENT_SUCCESS:
      return {
        ...state,
        loadingDetails: false,
        ...mapActivities(state, (activity) => {
          if (activity.activity_id === action.item.id && activity.type === action.item.type) {
            if (activity.comments) {
              activity.comments.push(action.item.res);
              activity.comment_count = activity.comments.length;
            } else {
              activity.comment_count = (activity.comment_count || 0) + 1;
            }
            activity.loading = false;
          }
          return activity;
        })
      };
    case ActivityTypes.POST_ACTIVITY_COMMENT_FAILURE:
      return {
        ...state,
        ...mapActivities(state, (activity) => {
          if (activity.activity_id === action.error.id && activity.type === action.error.type) {
            activity.loading = false;
            activity.error = action.error.error.message;
          }
          return activity;
        })
      };

    case ActivityTypes.CLOSE_EVENT_REQUEST:
      return {
        ...state
      };
    case ActivityTypes.CLOSE_EVENT_SUCCESS:
      return {
        ...state
      };
    case ActivityTypes.CLOSE_EVENT_FAILURE:
      return {
        ...state
      };
    case ActivityTypes.PUT_ACTIVITY_COMMENT_REQUEST:
      return {
        ...state,
        commentEditLoading: true
      };
    case ActivityTypes.PUT_ACTIVITY_COMMENT_SUCCESS:
      return {
        ...state,
        commentEditLoading: false,
        loadingDetails: false,
        ...mapActivities(state, (activity) => {
          if (activity.activity_id === action.item.id && activity.type === action.item.type) {
            const idx = activity.comments.findIndex((comment => comment.comment_id === action.item.res.comment_id));
            activity.comments[idx] = action.item.res;
            activity.loading = false;
          }
          return activity;
        })
      };
    case ActivityTypes.PUT_ACTIVITY_COMMENT_FAILURE:
      return {
        ...state,
        commentEditLoading: false,
        ...mapActivities(state, (activity) => {
          if (activity.activity_id === action.error.id && activity.type === action.error.type) {
            activity.loading = false;
            activity.error = action.error.error.message;
          }
          return activity;
        })
      };

    case ActivityTypes.DELETE_ACTIVITY_COMMENT_REQUEST:
      return {
        ...state,
        commentDeleteLoading: true
      };
    case ActivityTypes.DELETE_ACTIVITY_COMMENT_SUCCESS:
      return {
        ...state,
        commentEditLoading: false,
        loadingDetails: false,
        ...mapActivities(state, (activity) => {
          if (activity.activity_id === action.item.id && activity.type === action.item.type) {
            const idx = activity.comments.findIndex((comment => comment.comment_id === action.item.res.comment_id));
            activity.comments.splice(idx, 1);
            activity.loading = false;
          }
          return activity;
        }),
        commentDeleteLoading: false
      };
    case ActivityTypes.DELETE_ACTIVITY_COMMENT_FAILURE:
      return {
        ...state,
        commentDeleteLoading: false,
        ...mapActivities(state, (activity) => {
          if (activity.activity_id === action.error.id && activity.type === action.error.type) {
            activity.loading = false;
            activity.error = action.error.error.message;
          }
          return activity;
        })
      };

    case ActivityTypes.DELETE_NOTE_REQUEST:
      return {
        ...state
      };
    case ActivityTypes.DELETE_NOTE_SUCCESS: {
      const gone = isActivity('note', action.note_id);
      return removeActivities(state, gone);
    }
    case ActivityTypes.DELETE_NOTE_FAILURE:
      return {
        ...state
      };
    case ActivityTypes.DELETE_TASK_SUCCESS: {
      const gone = isActivity('task', action.taskId);
      return removeActivities(state, gone);
    }
    case ActivityTypes.DELETE_REPAIR_SUCCESS: {
      const gone = isActivity('repair', action.repairId);
      return removeActivities(state, gone);
    }
    case ActivityTypes.UPDATE_ACTIVITY_REQUEST:
      return {
        ...state
      };
    case ActivityTypes.UPDATE_ACTIVITY_SUCCESS: {
      if (['observation', 'remove_from_observation'].includes(action.action)) return state;
      const deltas = readDeltas(state, action.payload, action.action);
      return {
        ...state,
        groups: applyGroupDeltas(state.groups, deltas),
        ...mapActivities(state, (activity) => {
          if (action.payload.find(a => a.type === activity.type && a.activity_id === activity.activity_id)) {
            if (action.action === 'read') activity.read = true;
            else if (action.action === 'unread') activity.read = false;
          }
          return activity;
        })
      };
    }
    case ActivityTypes.UPDATE_ACTIVITY_FAILURE:
      return {
        ...state
      };
    case ActivityTypes.UPDATE_ACTIVITY_AI_STATUS:
      return {
        ...state,
        ...mapActivities(state, (activity) => {
          // AI analysis is for events only, and ids are per table.
          if (activity.type === 'event' && activity.activity_id === action.payload.activityId) {
            return {
              ...activity,
              ai_analysis_status: action.payload.aiAnalysisStatus,
              ai_analysis: action.payload.aiAnalysis
            };
          }
          return activity;
        })
      };
    default:
      return state;
  }
};
